import React from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { Tab, Tabs } from '@mui/material';
import { useTranslate } from 'react-admin';
import tokenService from '../services/tokenService';
import { canLicenseCourses } from './marketplaceVisibility';
import SellerOverviewPanel from '../components/Marketplace/Seller/SellerOverviewPanel';
import SellerListingsPanel from '../components/Marketplace/Seller/SellerListingsPanel';
import SellerStatementsPanel from '../components/Marketplace/Seller/SellerStatementsPanel';
import SellerBillingPanel from '../components/Marketplace/Seller/SellerBillingPanel';
import MaterialSymbol from '../components/common/MaterialSymbol';
import '../components/Marketplace/marketplace-design.css';

export type SellerSection = 'overview' | 'listings' | 'statements' | 'billing';

const PATHS: Record<SellerSection, string> = {
    overview: '/marketplace/seller',
    listings: '/marketplace/seller/listings',
    statements: '/marketplace/seller/statements',
    billing: '/marketplace/seller/billing',
};

/**
 * Seller-Dashboard (Marktplatz-Rework T5, Plan 3.7). Angebote darf der
 * Content-Manager pflegen; Übersicht, Abrechnungen und Rechnungsdaten/PayPal
 * verlangen Company-Admin (wie die zugehörigen API-Endpunkte).
 */
const MarketplaceSellerPage: React.FC<{ section?: SellerSection }> = ({ section = 'overview' }) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const isAdmin = canLicenseCourses(tokenService.getRoles());

    if (!isAdmin && section !== 'listings') {
        return <Navigate to={PATHS.listings} replace />;
    }

    const sections: SellerSection[] = isAdmin ? ['overview', 'listings', 'statements', 'billing'] : ['listings'];

    return (
        <div className="mk-page">
            <div className="mk-head">
                <h1 className="oa-page-title" style={{ margin: 0 }}>{translate('marketplaceSeller.title')}</h1>
                <div className="mk-acts">
                    <Link to="/marketplace" className="mk-btn-o">
                        <MaterialSymbol name="shopping_bag" />
                        {translate('marketplaceSeller.openMarketplace')}
                    </Link>
                    <button type="button" className="mk-btn-s" onClick={() => navigate(`${PATHS.listings}?new=1`)}>
                        <MaterialSymbol name="add" />
                        {translate('marketplaceSeller.listings.new')}
                    </button>
                </div>
            </div>
            <p className="mk-sub">{translate('marketplaceSeller.subtitle')}</p>

            <Tabs
                value={section}
                variant="scrollable"
                scrollButtons={false}
                aria-label={translate('marketplaceSeller.title')}
                className="mk-tabs"
            >
                {sections.map((s) => (
                    <Tab key={s} value={s} component={Link} to={PATHS[s]} disableRipple label={translate(`marketplaceSeller.nav.${s}`)} />
                ))}
            </Tabs>

            {section === 'overview' && <SellerOverviewPanel />}
            {section === 'listings' && <SellerListingsPanel />}
            {section === 'statements' && <SellerStatementsPanel />}
            {section === 'billing' && <SellerBillingPanel />}
        </div>
    );
};

export default MarketplaceSellerPage;
